import { useCallback, useEffect, useState } from 'react';

export type Theme = 'cactusds' | 'alternative' | 'camelia';

const STORAGE_KEY = 'cds_theme';
const DEFAULT_THEME: Theme = 'cactusds';

export const isThemeSwitcherEnabled = (): boolean =>
    process.env.REACT_APP_THEME_SWITCHER_ENABLED !== 'false';

const applyTheme = (theme: Theme): void => {
    document.documentElement.setAttribute('data-theme', theme);
};

const getStoredTheme = (): Theme => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
        if (stored === 'cactusds' || stored === 'alternative' || stored === 'camelia') return stored;
    } catch {}
    return DEFAULT_THEME;
};

const useTheme = () => {
    const [theme, setThemeState] = useState<Theme>(getStoredTheme);

    // Apply on mount and whenever theme changes
    useEffect(() => {
        applyTheme(theme);
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {}
    }, [theme]);

    const setTheme = useCallback((next: Theme) => {
        if (!isThemeSwitcherEnabled()) return;
        setThemeState(next);
    }, []);

    const toggleTheme = useCallback(() => {
        if (!isThemeSwitcherEnabled()) return;
        setThemeState(prev => (prev === 'cactusds' ? 'alternative' : 'cactusds'));
    }, []);

    return { theme, setTheme, toggleTheme };
};

export default useTheme;
